import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PiMapPinAreaFill } from "react-icons/pi";
import { FaRegMap } from "react-icons/fa";
import { FaCircleArrowLeft } from "react-icons/fa6";
import { LuBriefcaseBusiness } from "react-icons/lu";
import { Plus, Trash2, CheckCircle2, Circle, Search, ChevronDown, Loader2 } from 'lucide-react';
import { useI18n } from '@/i18n';
import { useAuthStore } from '@/store/authStore';
import { supabase } from '@/utils/supabase';
import { useResilientAction } from '@/hooks/useResilientAction';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { FaEdit } from "react-icons/fa";
import { RiHome9Line } from "react-icons/ri";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

import { BANGLADESH_CITIES, CITY_AREAS, getLocationName } from '@/data/bangladesh-locations';

interface Address {
  id: string;
  label: string;
  street: string;
  area: string;
  city: string;
  postal_code?: string;
  is_default: boolean;
  type: 'home' | 'work' | 'other';
}

export function Address() {
  const { currentLang } = useI18n();
  const navigate = useNavigate();
  const { user, profile, fetchProfile, isLoading: isAuthLoading } = useAuthStore();
  
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  // Form State
  const [label, setLabel] = useState(currentLang === 'bn' ? 'বাসা' : 'Home');
  const [street, setStreet] = useState('');
  const [area, setArea] = useState('');
  const [city, setCity] = useState('');

  const [addressType, setAddressType] = useState<'home' | 'work' | 'other'>('home');
  const [citySearch, setCitySearch] = useState('');
  const [areaSearch, setAreaSearch] = useState('');
  const [cityOpen, setCityOpen] = useState(false);
  const [areaOpen, setAreaOpen] = useState(false);

  useEffect(() => {
    if (!user && !isAuthLoading) {
      navigate('/login');
    }
  }, [user, navigate, isAuthLoading]);

  // Derive addresses directly from profile to avoid sync state issues
  let addresses: Address[] = [];
  if (profile?.address) {
    if (Array.isArray(profile.address)) {
      addresses = profile.address as Address[];
    } else if (typeof profile.address === 'object' && Object.keys(profile.address).length > 0) {
      const legacy = profile.address as Partial<Address>;
      addresses = [{
        id: legacy.id || 'default',
        label: legacy.label || (currentLang === 'bn' ? 'মুখ্য ঠিকানা' : 'Primary Address'),
        street: legacy.street || '',
        area: legacy.area || '',
        city: legacy.city || '',
        is_default: true,
        type: legacy.type || 'home'
      }];
    }
  }

  const isLoading = isAuthLoading && !profile;

  const handleEdit = (addr: Address) => {
    setEditingId(addr.id);
    setLabel(addr.label);
    setStreet(addr.street);
    setArea(addr.area);
    setCity(addr.city);

    setAddressType(addr.type);
    setIsAdding(true);
  };

  const { isLoading: isSettingDefault, execute: resilientSetDefault } = useResilientAction(
    async (signal: AbortSignal, id: string) => {
      if (!user || !addresses.length) return;
      
      const updatedAddresses = addresses.map(addr => ({
        ...addr,
        is_default: addr.id === id
      }));

      const { error } = await supabase
        .from('profiles')
        .update({ address: updatedAddresses })
        .eq('id', user.id)
        .abortSignal(signal);

      if (error) throw error;
      await fetchProfile();
      toast.success(currentLang === 'bn' ? 'ডিফল্ট ঠিকানা সেট করা হয়েছে' : 'Default address set');
    }
  );

  const handleSetDefault = async (id: string) => {
    await resilientSetDefault(id);
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setLabel(currentLang === 'bn' ? 'বাসা' : 'Home');
    setStreet('');
    setArea('');
    setCity('');

    setAddressType('home');
    setCitySearch('');
    setAreaSearch('');
  };

  const { isLoading: isSavingAddress, execute: resilientSaveAddress } = useResilientAction(
    async (signal: AbortSignal) => {
      let updatedAddresses: Address[] = [...addresses];
      
      const newAddress: Address = {
        id: editingId || crypto.randomUUID(),
        label,
        street,
        area,
        city,
        is_default: editingId ? (addresses.find(a => a.id === editingId)?.is_default || false) : (addresses.length === 0),
        type: addressType
      };

      if (editingId) {
        updatedAddresses = updatedAddresses.map(a => a.id === editingId ? newAddress : a);
      } else {
        updatedAddresses.push(newAddress);
      }

      if (updatedAddresses.length === 1) {
        updatedAddresses[0].is_default = true;
      }

      const { error } = await supabase
        .from('profiles')
        .update({ address: updatedAddresses })
        .eq('id', user!.id)
        .abortSignal(signal);

      if (error) throw error;
      
      await fetchProfile();
      handleCancel();
      toast.success(currentLang === 'bn' 
        ? (editingId ? 'ঠিকানা আপডেট হয়েছে' : 'ঠিকানা সংরক্ষিত হয়েছে') 
        : (editingId ? 'Address updated' : 'Address saved'));
      return true;
    }
  );

  const handleSaveAddress = async () => {
    if (!user) return;
    if (!label || !street || !area || !city) {
      toast.error(currentLang === 'bn' ? 'অনুগ্রহ করে সব তথ্য দিন' : 'Please fill all fields');
      return;
    }

    await resilientSaveAddress();
  };

  const { isLoading: isDeletingAddress, execute: resilientDeleteAddress } = useResilientAction(
    async (signal: AbortSignal, id: string) => {
      const updatedAddresses = addresses.filter(a => a.id !== id);
      
      if (addresses.find(a => a.id === id)?.is_default && updatedAddresses.length > 0) {
        updatedAddresses[0].is_default = true;
      }

      const { error } = await supabase
        .from('profiles')
        .update({ address: updatedAddresses.length > 0 ? updatedAddresses : null })
        .eq('id', user!.id)
        .abortSignal(signal);

      if (error) throw error;
      await fetchProfile();
      toast.success(currentLang === 'bn' ? 'ঠিকানা মুছে ফেলা হয়েছে' : 'Address deleted');
    }
  );

  const handleDeleteAddress = async (id: string) => {
    if (!user) return;
    await resilientDeleteAddress(id);
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'home': return <RiHome9Line className="w-4 h-4" />;
      case 'work': return <LuBriefcaseBusiness className="w-4 h-4" />;
      default: return <FaRegMap className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-[100dvh] bg-gray-50 font-sans pb-32 lg:pb-10">
      {/* Premium Header — Gold Standard */}
      <header className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-xl border-b border-gray-100/50 shadow-[0_4px_20px_rgba(0,0,0,0.03)] transition-all">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-xl hover:bg-gray-50 text-black transition-all active:scale-95"
            onClick={() => navigate(-1)}
          >
            <FaCircleArrowLeft className="h-6 w-6" />
          </Button>
          <h1 className="text-lg font-black tracking-tight text-center flex-1">
            {currentLang === 'bn' ? 'আপনার ঠিকানা' : 'Delivery Addresses'}
          </h1>
          <div className="w-10" /> {/* Spacer */}
        </div>
      </header>

      <div className="container mx-auto px-4 mt-6 max-w-lg">
        {!isAdding ? (
          <div className="space-y-4">
            {isLoading ? (
              <div className="flex justify-center py-6">
                <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
              </div>
            ) : addresses.length > 0 ? (
              addresses.map((addr) => (
                <Card key={addr.id} className="border-none shadow-sm rounded-2xl overflow-hidden">
                  <CardContent className="px-3 -my-3">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary">
                          {getIcon(addr.type)}
                        </div>
                        <span className="font-bold text-black">{addr.label}</span>
                        {addr.is_default && (
                          <Badge variant="secondary" className="bg-emerald-100 text-black border-emerald-200 text-[10px] font-bold">
                            DEFAULT
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button 
                          onClick={() => handleEdit(addr)}
                          className="p-2 text-primary hover:text-primary hover:bg-primary/5 rounded-full transition-all"
                        >
                         <FaEdit className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleDeleteAddress(addr.id)}
                          className="p-2 text-red-500 hover:text-red-500 hover:bg-red-50 rounded-full transition-all disabled:opacity-50"
                          disabled={isDeletingAddress}
                        >
                          {isDeletingAddress ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <p className="text-sm text-black leading-snug">
                      {addr.street}, {getLocationName(addr.area, currentLang)}, {getLocationName(addr.city, currentLang)}
                    </p>
                    
                    <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                      {addr.is_default ? (
                        <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                          <CheckCircle2 className="w-4 h-4 ml-1" />
                          {currentLang === 'bn' ? 'নির্বাচিত ডেলিভারি লোকেশন' : 'Selected for delivery'}
                        </div>
                      ) : (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleSetDefault(addr.id)}
                          disabled={isSettingDefault}
                          className="flex items-center gap-2 text-gray-600 font-semibold text-sm -my-1 hover:bg-white"
                        >
                          {<Circle className="w-4 h-4 -ml-2" />}
                          {currentLang === 'bn' ? 'ডিফল্ট হিসেবে সেট করুন' : 'Set as Default'}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="bg-white rounded-3xl p-10 text-center border-2 border-dashed border-gray-200">
                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <PiMapPinAreaFill className="w-8 h-8 text-gray-600" />
                </div>
                <h3 className="font-bold text-black">No address saved</h3>
                <p className="text-sm text-gray-700 mt-1">Add a delivery address to continue</p>
              </div>
            )}

            <Button 
                onClick={() => setIsAdding(true)}
                className="w-full h-14 rounded-2xl font-bold gap-2 mt-4 shadow-lg shadow-primary/20"
            >
              <Plus className="w-5 h-5" />
              {currentLang === 'bn' ? 'নতুন ঠিকানা যোগ করুন' : 'Add New Address'}
            </Button>
          </div>
        ) : (
          <Card className="border-none shadow-sm rounded-3xl overflow-hidden animate-in slide-in-from-bottom-5">
            <CardContent className="p-6">
              <h2 className="text-lg font-bold mb-6">
                {editingId 
                  ? (currentLang === 'bn' ? 'ঠিকানা পরিবর্তন করুন' : 'Edit Delivery Address')
                  : (currentLang === 'bn' ? 'নতুন ঠিকানা' : 'Add Detail Address')
                }
              </h2>
              
              <div className="space-y-6">
                <div className="space-y-3">
                  <Label className="text-xs font-bold text-gray-700 uppercase tracking-wider">Address Type</Label>
                  <div className="flex gap-3">
                    {[
                      { id: 'home', icon: <RiHome9Line className="w-4 h-4" />, label: currentLang === 'bn' ? 'বাসা' : 'Home' },
                      { id: 'work', icon: <LuBriefcaseBusiness className="w-4 h-4" />, label: currentLang === 'bn' ? 'অফিস' : 'Work' },
                      { id: 'other', icon: <FaRegMap className="w-4 h-4" />, label: currentLang === 'bn' ? 'অন্যান্য' : 'Other' }
                    ].map((type) => (
                      <button
                        key={type.id}
                        onClick={() => {
                          setAddressType(type.id as Address['type']);
                          setLabel(type.label);
                        }}
                        className={`flex-1 flex items-center justify-center gap-2 p-2.5 rounded-xl border-2 transition-all ${
                          addressType === type.id 
                            ? 'border-primary bg-primary/5 text-primary' 
                            : 'border-gray-100 bg-white text-gray-600 hover:border-gray-200'
                        }`}
                      >
                        {type.icon}
                        <span className="text-[11px] font-bold uppercase tracking-tight">{type.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold text-gray-700 uppercase tracking-wider">City</Label>
                  <Popover open={cityOpen} onOpenChange={setCityOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={cityOpen}
                        className="w-full h-12 rounded-xl bg-gray-50 border-none hover:bg-gray-50 focus:ring-1 focus:ring-primary/20 justify-between font-normal text-left"
                      >
                        {city ? getLocationName(city, currentLang) : 'Select City'}
                        <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0 rounded-xl" align="start">
                      <div className="sticky top-0 bg-white p-2 border-b border-gray-100 z-10">
                        <div className="relative">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                          <Input 
                            placeholder={currentLang === 'bn' ? 'শহর খুঁজুন...' : 'Search city...'}
                            value={citySearch}
                            onChange={(e) => setCitySearch(e.target.value)}
                            className="pl-8 h-9 text-xs border-none bg-gray-50 focus-visible:ring-1 focus-visible:ring-primary/20 rounded-lg"
                          />
                        </div>
                      </div>
                      <div className="max-h-60 overflow-y-auto">
                        {BANGLADESH_CITIES.filter(c => 
                          c.nameen?.toLowerCase().includes(citySearch.toLowerCase()) || 
                          c.namebn?.includes(citySearch)
                        ).map((c) => (
                          <div
                            key={c.id}
                            className="hover:bg-primary/5 py-3 px-4 font-medium cursor-pointer text-sm transition-colors"
                            onClick={() => {
                              setCity(c.nameen);
                              setArea('');
                              setCityOpen(false);
                              setCitySearch('');
                            }}
                          >
                            {currentLang === 'bn' ? c.namebn : c.nameen}
                          </div>
                        ))}
                        {BANGLADESH_CITIES.filter(c => 
                          c.nameen?.toLowerCase().includes(citySearch.toLowerCase()) || 
                          c.namebn?.includes(citySearch)
                        ).length === 0 && (
                          <div className="py-6 text-center text-xs text-gray-400">
                            {currentLang === 'bn' ? 'কোনো শহর পাওয়া যায়নি' : 'No cities found'}
                          </div>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold text-gray-700 uppercase tracking-wider">Area</Label>
                  {city && CITY_AREAS[city] ? (
                    <Popover open={areaOpen} onOpenChange={setAreaOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={areaOpen}
                          className="w-full h-12 rounded-xl bg-gray-50 border-none hover:bg-gray-50 focus:ring-1 focus:ring-primary/20 justify-between font-normal text-left"
                        >
                          {area ? getLocationName(area, currentLang) : (currentLang === 'bn' ? "এলাকা নির্বাচন করুন" : "Select Area")}
                          <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 rounded-xl" align="start">
                        <div className="sticky top-0 bg-white p-2 border-b border-gray-100 z-10">
                          <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                            <Input 
                              placeholder={currentLang === 'bn' ? 'এলাকা খুঁজুন...' : 'Search area...'}
                              value={areaSearch}
                              onChange={(e) => setAreaSearch(e.target.value)}
                              className="pl-8 h-9 text-xs border-none bg-gray-50 focus-visible:ring-1 focus-visible:ring-primary/20 rounded-lg"
                            />
                          </div>
                        </div>
                        <div className="max-h-60 overflow-y-auto">
                          {CITY_AREAS[city].filter(a => 
                            a.nameen?.toLowerCase().includes(areaSearch.toLowerCase()) || 
                            a.namebn?.includes(areaSearch)
                          ).map((a) => (
                            <div
                              key={a.id}
                              className="hover:bg-primary/5 py-3 px-4 font-medium cursor-pointer text-sm transition-colors"
                              onClick={() => {
                                setArea(a.nameen);
                                setAreaOpen(false);
                                setAreaSearch('');
                              }}
                            >
                              {currentLang === 'bn' ? a.namebn : a.nameen}
                            </div>
                          ))}
                          {CITY_AREAS[city].filter(a => 
                            a.nameen?.toLowerCase().includes(areaSearch.toLowerCase()) || 
                            a.namebn?.includes(areaSearch)
                          ).length === 0 && (
                            <div className="py-6 text-center text-xs text-gray-400">
                              {currentLang === 'bn' ? 'কোনো এলাকা পাওয়া যায়নি' : 'No areas found'}
                            </div>
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <Input 
                      placeholder={currentLang === 'bn' ? "যেমন: উত্তরা" : "e.g. Uttara"}
                      value={area}
                      onChange={(e) => setArea(e.target.value)}
                      disabled={!city}
                      className="h-12 rounded-xl bg-gray-50 border-none focus-visible:ring-1 focus-visible:ring-primary/20 disabled:opacity-50"
                    />
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold text-gray-700 uppercase tracking-wider">Street Address</Label>
                  <Input 
                    placeholder="House 12, Road 5"
                    value={street}
                    onChange={(e) => setStreet(e.target.value)}
                    className="h-12 rounded-xl bg-gray-50 border-none focus-visible:ring-1 focus-visible:ring-primary/20"
                  />
                </div>



                <div className="flex items-center gap-3 pt-2">
                  <Button 
                    type="button"
                    variant="outline" 
                    className="flex-1 h-12 rounded-xl text-gray-700 font-bold"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="button"
                    className="flex-1 h-12 rounded-xl font-bold"
                    onClick={handleSaveAddress}
                    disabled={isSavingAddress}
                  >
                    {isSavingAddress ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {isSavingAddress ? 'Saving...' : 'Save Address'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
